/**
 * interfaces
 */
import { IUnitTreeModel } from './interfaces';
import { IUnitTreeDto } from '../../interfaces';

/**
 * View model for a unit tree node (GET units/tree/{id}).
 *
 * Wraps the raw {@link IUnitTreeDto} and exposes every field as a
 * camelCase getter. Children are recursively wrapped into {@link UnitTreeModel}
 * instances so consumers never touch the raw DTO shape at any depth.
 *
 * @example
 * ```ts
 * const model = new UnitTreeModel(dto);
 * console.log(model.label);              // "Zone 01"
 * console.log(model.hasChildren);        // true
 * console.log(model.children[0].label);  // "Floor 1"
 * ```
 */
export class UnitTreeModel implements IUnitTreeModel {

    /** Nested child unit nodes, recursively mapped to view-models. */
    public readonly children: UnitTreeModel[];

    /**
     * @param dto - Raw unit tree node DTO from the API.
     */
    constructor(public readonly dto: IUnitTreeDto) {
        const _children: IUnitTreeDto[] = dto.children ?? []
        this.children = _children.map((childDto) => new UnitTreeModel(childDto));
    }

    /** @inheritdoc */
    public get id(): string {
        return this.dto.id;
    }

    /** @inheritdoc */
    public get label(): string {
        return this.dto.label;
    }

    /** @inheritdoc */
    public get level(): number {
        return this.dto.level;
    }

    /** @inheritdoc */
    public get cssClass(): string | null {
        return this.dto.css_class;
    }

    /** @inheritdoc */
    public get htmlHexColor(): string | null {
        return this.dto.html_hex_color;
    }

    /** @inheritdoc */
    public get hasChildren(): boolean {
        return this.children.length > 0;
    }

}
